import { isTuningExperimentControl } from '@playcastdotio/PeerCore';
import type { TuningExperimentControl, TuningExperimentState } from '@playcastdotio/PeerCore';
import type { IHostTuningControl } from '../HostTuningContracts';

/** Remote debug UI uses the same authenticated host authority and experiment owner as the local API. */
export class TuningExperimentGateway {
  private readonly requests = new Map<string, { content: string; result: Promise<TuningExperimentState> }>();
  constructor(private readonly options: { host: IHostTuningControl; authorize(control: TuningExperimentControl): boolean }) {}

  handle(control: TuningExperimentControl): Promise<TuningExperimentState> {
    const request = control.action === 'start' ? control.request : control;
    if (!isTuningExperimentControl(control) || !this.options.authorize(control)) {
      return Promise.resolve(this.error(request.requestId, request.sessionId, 'unauthorized'));
    }
    const key = `${request.sessionId}:${request.requestId}`, content = JSON.stringify(control), previous = this.requests.get(key);
    if (previous) return previous.content === content ? previous.result : Promise.resolve(this.error(request.requestId, request.sessionId, 'request-conflict'));
    const pinned = JSON.parse(content) as TuningExperimentControl;
    const result = this.execute(pinned).catch(error => this.error(request.requestId, request.sessionId,
      error instanceof Error ? error.message : 'experiment failed'));
    this.requests.set(key, { content, result });
    if (this.requests.size > 64) this.requests.delete(this.requests.keys().next().value as string);
    return result;
  }

  private async execute(control: TuningExperimentControl): Promise<TuningExperimentState> {
    const request = control.action === 'start' ? control.request : control;
    let snapshot;
    if (control.action === 'start') snapshot = await this.options.host.startExperiment(control.request);
    else {
      snapshot = await this.options.host.experiment(control.experimentId);
      if (!snapshot || snapshot.sessionId !== request.sessionId) return this.error(request.requestId, request.sessionId, 'unknown-experiment');
      if (control.action === 'cancel') snapshot = await this.options.host.cancelExperiment(control.experimentId, control.reason,
        { requestId: control.requestId, sessionId: control.sessionId, leaseId: control.leaseId });
    }
    if (!snapshot || snapshot.sessionId !== request.sessionId) return this.error(request.requestId, request.sessionId, 'unknown-experiment');
    return { requestId: request.requestId, sessionId: request.sessionId, snapshot };
  }
  private error(requestId: string, sessionId: string, message: string): TuningExperimentState {
    return { requestId, sessionId, error: { code: message.slice(0, 128), message: message.slice(0, 2_048) } };
  }
}
